'use client'

import { Loader2, Search, Shield } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useMemo, useState, useTransition } from 'react'
import { useTranslations } from '@/lib/i18n/client'
import { cn, FILTER_PENDING_CLASSES } from '@/lib/utils'

type CategoriaOption = {
  slug: string
  label: string
}

export function HeroQuickSearch({
  fabricantes,
  categoriaOptions,
  modeloOptions,
}: {
  fabricantes: { slug: string; nome: string }[]
  categoriaOptions: CategoriaOption[]
  modeloOptions: {
    slug: string
    label: string
    fabricante_slug: string
    categoria_slug: string
  }[]
}) {
  const { t } = useTranslations()
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const [categoria, setCategoria] = useState('')
  const [fabricante, setFabricante] = useState('')
  const [modelo, setModelo] = useState('')

  const visibleFabricantes = useMemo(() => {
    if (!categoria) return fabricantes
    const allowed = new Set(
      modeloOptions
        .filter((m) => m.categoria_slug === categoria)
        .map((m) => m.fabricante_slug),
    )
    return fabricantes.filter((f) => allowed.has(f.slug))
  }, [fabricantes, modeloOptions, categoria])

  const visibleModelos = useMemo(
    () =>
      modeloOptions.filter((m) => {
        if (categoria && m.categoria_slug !== categoria) return false
        if (fabricante && m.fabricante_slug !== fabricante) return false
        return true
      }),
    [modeloOptions, categoria, fabricante],
  )

  const handleCategoriaChange = (novoSlug: string) => {
    setCategoria(novoSlug)
    if (!novoSlug) return
    if (
      fabricante &&
      !modeloOptions.some(
        (m) => m.fabricante_slug === fabricante && m.categoria_slug === novoSlug,
      )
    ) {
      setFabricante('')
    }
    if (
      modelo &&
      !modeloOptions.some(
        (m) => m.slug === modelo && m.categoria_slug === novoSlug,
      )
    ) {
      setModelo('')
    }
  }

  const handleSubmit = () => {
    const params = new URLSearchParams()
    if (categoria) params.set('categoria', categoria)
    if (fabricante) params.set('fabricante', fabricante)
    if (modelo) params.set('modelo', modelo)
    const qs = params.toString()
    startTransition(() => {
      router.push(qs ? `/aeronaves?${qs}` : '/aeronaves')
    })
  }

  const topCategorias = categoriaOptions.slice(0, 6)

  return (
    <div
      className={cn(
        'fade-up rounded-xl border-[0.5px] border-white/10 bg-white p-6',
        isPending && FILTER_PENDING_CLASSES,
      )}
      style={{ animationDelay: '0.15s' }}
      aria-busy={isPending}
    >
      <div>
        <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-500">
          {t.home.quickSearchLabel}
        </div>
        <div className="mt-0.5 font-heading text-[20px] font-medium text-navy-900">
          {t.home.quickSearchTitle}
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3">
        <div>
          <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.05em] text-slate-500">
            {t.home.quickSearchCategory}
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-1.5">
            <button
              type="button"
              onClick={() => handleCategoriaChange('')}
              aria-pressed={!categoria}
              className={
                'inline-flex min-h-12 cursor-pointer items-center rounded-full border-[0.5px] px-4 py-2 text-[13px] font-medium leading-tight transition-colors sm:min-h-10 ' +
                (!categoria
                  ? 'border-navy-700 bg-navy-50 text-navy-700'
                  : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50')
              }
            >
              {t.home.quickSearchAll}
            </button>
            {topCategorias.map((c) => {
              const selected = categoria === c.slug
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => handleCategoriaChange(selected ? '' : c.slug)}
                  aria-pressed={selected}
                  className={
                    'inline-flex min-h-12 cursor-pointer items-center rounded-full border-[0.5px] px-4 py-2 text-[13px] font-medium leading-tight transition-colors sm:min-h-10 ' +
                    (selected
                      ? 'border-navy-700 bg-navy-50 text-navy-700'
                      : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50')
                  }
                >
                  {c.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <div>
            <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.05em] text-slate-500">
              {t.home.quickSearchManufacturer}
            </div>
            <select
              value={fabricante}
              aria-label={t.home.quickSearchManufacturer}
              onChange={(e) => {
                const novo = e.target.value
                setFabricante(novo)
                if (
                  modelo &&
                  !modeloOptions.some(
                    (m) =>
                      m.slug === modelo &&
                      (!novo || m.fabricante_slug === novo) &&
                      (!categoria || m.categoria_slug === categoria),
                  )
                ) {
                  setModelo('')
                }
              }}
              className="h-12 w-full rounded-lg border-[0.5px] border-slate-300 bg-white px-3 text-[14px] text-slate-900 outline-none focus:border-navy-400 sm:h-10"
            >
              <option value="">{t.home.quickSearchAll}</option>
              {visibleFabricantes.map((f) => (
                <option key={f.slug} value={f.slug}>
                  {f.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.05em] text-slate-500">
              {t.home.quickSearchModel}
            </div>
            <select
              value={modelo}
              aria-label={t.home.quickSearchModel}
              onChange={(e) => setModelo(e.target.value)}
              className="h-12 w-full rounded-lg border-[0.5px] border-slate-300 bg-white px-3 text-[14px] text-slate-900 outline-none focus:border-navy-400 sm:h-10"
            >
              <option value="">{t.home.quickSearchAll}</option>
              {visibleModelos.map((m) => (
                <option key={m.slug} value={m.slug}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isPending}
          className="mt-1 inline-flex h-12 cursor-pointer items-center justify-center gap-2 rounded-lg bg-navy-800 px-5 text-[13px] font-medium uppercase tracking-[0.08em] text-white transition-colors hover:bg-navy-900 disabled:cursor-wait"
        >
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Search className="h-3.5 w-3.5" />
          )}
          {t.home.quickSearchSubmit}
        </button>
      </div>

      <div className="mt-4 flex items-start gap-2.5 border-t-[0.5px] border-slate-200 pt-4 text-[12px] text-slate-500">
        <Shield className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{t.home.quickSearchTrust}</span>
      </div>
    </div>
  )
}
